/**
 * A notificação de um push, montada a partir do payload do Web Push, e o recado que o service
 * worker manda às abas abertas. Fica fora do `sw.ts` para ser testada sem o escopo do service
 * worker (o `sw.ts` registra Workbox e listeners ao ser importado).
 *
 * Três payloads, distinguidos por `kind`:
 *  - a nota (sem `kind`): `{ id, status, numero, valor, autorizada_em, app_name, icon }`, com o
 *    texto montado aqui em pt-BR para não grudar mensagem na task Python;
 *  - o post do feed de documentos (`kind: 'document_post'`, E14): `{ post_id, client_id, title,
 *    body, url, tag, app_name, icon }`, com título (o nome curto do escritório) e texto (o resumo
 *    do post) prontos no backend (`documents/notifications.py`);
 *  - o lembrete das guias (`kind: 'guide_reminder'`, E16): `{ client_id, stage, guide_ids, title,
 *    body, url, tag, app_name, icon }`, um por empresa e etapa, também com o texto pronto.
 *
 * `app_name` é o nome do escritório e `icon` a URL pública do logo, ou `null`. O post e o lembrete
 * abrem a tela da empresa deles (`?empresa=<id>`). O service worker anterior a E16 não conhece o
 * lembrete e o mostra pelo caminho da nota, que usa o `title`, o `body` e a `url` do payload.
 */

// Import relativo: o build do service worker não depende do alias `~` da configuração do app.
import { ROUTES, companyPath } from '../routes';

export const DOCUMENT_POST_KIND = 'document_post';
export const GUIDE_REMINDER_KIND = 'guide_reminder';

export interface InvoicePushPayload {
  /** A nota não traz `kind`; o campo existe só para a união se distinguir por ele. */
  kind?: undefined;
  id?: string;
  status?: string;
  numero?: number | null;
  valor?: string | number | null;
  autorizada_em?: string | null;
  /** Nome de exibição do escritório: título quando o status não tem um próprio. */
  app_name?: string | null;
  /** URL pública do logo do escritório; sem ele, o ícone do app. */
  icon?: string | null;
  url?: string;
  title?: string;
  body?: string;
}

export interface DocumentPostPayload {
  kind: typeof DOCUMENT_POST_KIND;
  post_id: string;
  client_id: number;
  /** O nome curto do escritório que publicou. */
  title: string;
  /** O resumo do post: um documento pelo título, vários pela contagem e os títulos. */
  body: string;
  /** A tela que o toque abre; sem ela, o feed. */
  url?: string | null;
  /** `post-<id>`: dois pushes do mesmo post viram uma notificação só. */
  tag: string;
  app_name?: string | null;
  icon?: string | null;
}

/** A etapa do lembrete: antes do vencimento, no dia e depois dele (`documents/reminders.py`). */
export type GuideReminderStage = 'BEFORE' | 'DUE' | 'AFTER';

export interface GuideReminderPayload {
  kind: typeof GUIDE_REMINDER_KIND;
  /** A empresa das guias: o toque abre as guias dela. */
  client_id: number;
  stage: GuideReminderStage;
  /** As guias que este lembrete avisa. */
  guide_ids: number[];
  /** O nome curto do escritório. */
  title: string;
  /** O texto pronto: "Vence hoje: DAS 09/2026, R$ 1.234,56", ou a contagem e as guias. */
  body: string;
  /** A tela que o toque abre; sem ela, as guias da empresa. */
  url?: string | null;
  /** `guides-<empresa>-<etapa>-<dia>`: o mesmo lembrete repetido vira uma notificação só. */
  tag: string;
  app_name?: string | null;
  icon?: string | null;
}

export type PushPayload = InvoicePushPayload | DocumentPostPayload | GuideReminderPayload;

// `renotify` é spec do Web Push mas ainda não está no lib.dom.d.ts —
// estende localmente para o TS aceitar o campo.
export type PushNotificationOptions = NotificationOptions & { renotify?: boolean };

export interface PushNotification {
  title: string;
  options: PushNotificationOptions;
}

/** O recado às abas abertas: cada uma invalida o cache do que o push mudou. */
export const PUSH_MESSAGE_TYPE = {
  invoice: 'INVOICE_PUSH',
  documentPost: 'DOCUMENT_PUSH',
  guideReminder: 'GUIDE_PUSH',
} as const;

export type PushMessageType = (typeof PUSH_MESSAGE_TYPE)[keyof typeof PUSH_MESSAGE_TYPE];

export interface InvoicePushMessage {
  type: typeof PUSH_MESSAGE_TYPE.invoice;
  invoiceId?: string;
  status?: string;
}

export interface DocumentPushMessage {
  type: typeof PUSH_MESSAGE_TYPE.documentPost;
  postId: string;
  clientId: number;
}

export interface GuidePushMessage {
  type: typeof PUSH_MESSAGE_TYPE.guideReminder;
  clientId: number;
  stage: GuideReminderStage;
}

export type PushMessage = InvoicePushMessage | DocumentPushMessage | GuidePushMessage;

const PUSH_MESSAGE_TYPES: readonly string[] = Object.values(PUSH_MESSAGE_TYPE);

const APP_ICON = '/icons/icon-192.png';
const FALLBACK_TITLE = 'Nota fiscal';
const FALLBACK_BODY = 'Atualização disponível';

const STATUS_TITLE: Record<string, string> = {
  AUTHORIZED: 'Nota autorizada',
  PROCESSING: 'Nota em processamento',
  CANCELLED: 'Nota cancelada',
  REJECTED: 'Nota rejeitada',
  ERROR: 'Falha ao emitir nota',
  DRAFT: 'Rascunho salvo',
};

function brl(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function isDocumentPost(payload: PushPayload): payload is DocumentPostPayload {
  return payload.kind === DOCUMENT_POST_KIND;
}

function isGuideReminder(payload: PushPayload): payload is GuideReminderPayload {
  return payload.kind === GUIDE_REMINDER_KIND;
}

function invoiceNotification(payload: InvoicePushPayload): PushNotification {
  const status = payload.status ?? '';
  const title = payload.title ?? STATUS_TITLE[status] ?? payload.app_name ?? FALLBACK_TITLE;
  const numeroLabel = payload.numero ? `Nº ${payload.numero}` : '';
  const valorLabel = payload.valor ? `R$ ${brl(payload.valor)}` : '';
  const body = payload.body ?? [numeroLabel, valorLabel].filter(Boolean).join(' · ');

  return {
    title,
    options: {
      body: body || FALLBACK_BODY,
      icon: payload.icon ?? APP_ICON,
      // O badge é a silhueta monocromática da barra de status: fica o do app, nunca o logo.
      badge: APP_ICON,
      tag: payload.id ? `invoice-${payload.id}` : 'invoice',
      renotify: true,
      data: {
        url: payload.url ?? (payload.id ? `/notas/${payload.id}` : '/notas'),
        invoiceId: payload.id,
        receivedAt: Date.now(),
      },
    },
  };
}

function documentPostNotification(payload: DocumentPostPayload): PushNotification {
  return {
    title: payload.title,
    options: {
      body: payload.body,
      icon: payload.icon ?? APP_ICON,
      badge: APP_ICON,
      tag: payload.tag,
      renotify: true,
      data: {
        url: payload.url ?? companyPath(ROUTES.documentos, payload.client_id),
        postId: payload.post_id,
        receivedAt: Date.now(),
      },
    },
  };
}

function guideReminderNotification(payload: GuideReminderPayload): PushNotification {
  return {
    title: payload.title,
    options: {
      body: payload.body,
      icon: payload.icon ?? APP_ICON,
      badge: APP_ICON,
      tag: payload.tag,
      renotify: true,
      data: {
        url: payload.url ?? companyPath(ROUTES.impostos, payload.client_id),
        clientId: payload.client_id,
        stage: payload.stage,
        receivedAt: Date.now(),
      },
    },
  };
}

export function buildNotification(payload: PushPayload): PushNotification {
  if (isDocumentPost(payload)) return documentPostNotification(payload);
  if (isGuideReminder(payload)) return guideReminderNotification(payload);
  return invoiceNotification(payload);
}

/** O recado que o service worker manda às abas abertas quando o push chega. */
export function pushMessageOf(payload: PushPayload): PushMessage {
  if (isDocumentPost(payload)) {
    return {
      type: PUSH_MESSAGE_TYPE.documentPost,
      postId: payload.post_id,
      clientId: payload.client_id,
    };
  }
  if (isGuideReminder(payload)) {
    return {
      type: PUSH_MESSAGE_TYPE.guideReminder,
      clientId: payload.client_id,
      stage: payload.stage,
    };
  }
  return { type: PUSH_MESSAGE_TYPE.invoice, invoiceId: payload.id, status: payload.status };
}

/**
 * Lê o `data` de uma mensagem do service worker: só os recados de push passam. Garante o `type`,
 * que é o que a aba lê para saber o que invalidar.
 */
export function isPushMessage(data: unknown): data is Pick<PushMessage, 'type'> {
  if (typeof data !== 'object' || data === null || !('type' in data)) return false;
  return typeof data.type === 'string' && PUSH_MESSAGE_TYPES.includes(data.type);
}
